import angular from "angular";

import { ApiItemType, StorageLogic, Usecase, UsecaseInSettings, UsecaseStatus, Usecases, UsecaseListItem } from "@salaxy/core";

import { SessionUsecase } from "./model";
import { SessionService } from "./SessionService";
import { UsecaseApiCrudObject } from "./ui/model";

/** Provides caching for usecases and resolves them to business objects. */
export class UsecasesService {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Usecases", "SessionService", "$sce"];

  constructor(private usecases: Usecases, private sessionService: SessionService, private $sce: angular.ISCEService) {
  }

  /**
   * Gets active usecases that are shown in listings (selection of usecase)
   * including type-specific default usecases (applied if no other usecase is defined).
   */
  public getActiveUsecases(type: ApiItemType = null, defaultOnly = false): UsecaseInSettings[] {
    const listInSettings: UsecaseInSettings[] = this.sessionService.session?.settings?.usecases || [];
    let filteredList = listInSettings.filter((x) => !type || type == x.itemType);
    if (defaultOnly) {
      filteredList = filteredList.filter((x) => x.status === UsecaseStatus.Default);
    }
    return filteredList;
  }

  /**
   * Gets the active items as list items. The list of all usecases is cached.
   * @param type Type of items to fetch.
   * TODO: The method currently supports max 50 active usecases (of all types).
   */
  public getActiveUsecaseListItems(type: ApiItemType = null): Promise<UsecaseListItem[]> {
    const filter = (x: UsecaseListItem) => (!type || type == x.entityType) && (x.status == UsecaseStatus.Active || x.status == UsecaseStatus.Default);
    if (this.cachedActiveListItems) {
      return Promise.resolve(this.cachedActiveListItems.filter(filter));
    }
    return this.usecases.getOData({ $filter: `status eq 'active' or status eq 'default'` }).then((result) => {
      this.cachedActiveListItems = result.items;
      return this.cachedActiveListItems.filter(filter);
    });
  }

  private cachedActiveListItems: UsecaseListItem[];

  /**
   * Gets the activity type of usecase from the settings.
   * @param uri Usecase URI
   * @returns Information on whether the usecase is active or not - or default usecase (also active).
   */
  public getActive(uri: string): UsecaseStatus {
    if (!uri) {
      return UsecaseStatus.Inactive;
    }
    if (uri.startsWith("http")) {
      uri = new URL(uri).pathname;
    }
    const item = this.sessionService.session?.settings?.usecases?.find((x) => x.uri == uri);
    if (!item || item.status === UsecaseStatus.Undefined ) {
      return UsecaseStatus.Inactive;
    }
    return item.status;
  }

  /**
   * Gets the order  of usecase from the settings.
   * @param uri Usecase URI
   * @returns Order number for the usecase, 0 if not set.
   */
   public getOrder(uri: string): number {
    if (!uri) {
      return 0;
    }
    if (uri.startsWith("http")) {
      uri = new URL(uri).pathname;
    }
    const item = this.sessionService.session?.settings?.usecases?.find((x) => x.uri == uri);
    return item?.order ?? 0;
  }

  /**
   * Assures that the usecase model is fetched and data model is initialized according to usecase.
   * @param type Type of business object
   * @param item Business object to initialize.
   * @param defaultUri Usecase uri if passed by the calling component (e.g. in URL or component attribute).
   * @returns The usecase with session / role related data or null if none is set.
   */
  public assureUsecase(type: ApiItemType, item: UsecaseApiCrudObject, defaultUri: string): Promise<SessionUsecase> {
    if (!item) {
      return Promise.resolve(null);
    }
    return this.getUsecaseForObject(type, item, defaultUri).then((usecase) => {
      // Prepare the usecase data model for data binding: Should either be in Core or in data binding logic (sxy-forms).
      item.usecase = item.usecase || {};
      item.usecase.data = item.usecase.data || {};
      if (!usecase) {
        return null;
      }
      if (usecase) {
        item.usecase.uri = usecase.uri;
        item.usecase.label = usecase.label;
        item.usecase.description = usecase.description;
      }
      return usecase;
    });
  }

  /**
   * Gets a usecase definition based on business object (that has usecase data item).
   * @param type Business object type
   * @param item The business objec to evaluate.
   * @param defaultUri Usecase uri if passed by the calling component (e.g. in URL or component attribute).
   * @returns The usecase with session / role related data or null if none is set.
   */
  public getUsecaseForObject(type: ApiItemType, item: UsecaseApiCrudObject, defaultUri: string): Promise<SessionUsecase> {
    if (!item) {
      return Promise.resolve(null);
    }
    if (type == ApiItemType.Calculation) {
      // HACK: Handle these exceptions on product level.
      const considerEmpty = [
        "palkkaus.fi/usecases/payroll-service/pro-calc",
        "palkkaus.fi/usecases/payroll-service/calc",
      ];
      if (item?.usecase?.uri && !considerEmpty.find((x) => item.usecase.uri == x)) {
        return this.getUsecase(item.usecase.uri);
      }
    }
    else if (item?.usecase?.uri) {
      return this.getUsecase(item.usecase.uri);
    }
    defaultUri = defaultUri || this.getActiveUsecases(type, true)[0]?.uri;
    return defaultUri ? this.getUsecase(defaultUri) : Promise.resolve(null);
  }

  /** Clears the usecase cache: Will be fetched from the server in next getUsecase(). */
  public clearCache(): void {
    this.cachedUsecases = {};
  }

  /**
   * Adds abilitity to set the contents of a usecase in cache.
   * This is used in dynamic preview in edit usecase scenarios.
   * @param newVersion A version of the usecase that is set into usecases cache.
   */
  public setToCache(newVersion: Usecase): void {
    const sessionUsecase = newVersion as SessionUsecase;
    sessionUsecase.isLoaded = true;
    sessionUsecase.exists = true;
    if (sessionUsecase.views?.length && newVersion.uri) {
      // Later, we may have some profiling logic here: Different UI for different roles etc.
      sessionUsecase.defaultView = this.$sce.trustAsResourceUrl(this.usecases.getResourceUrl(newVersion.uri, "html"));
    }
    this.cachedUsecases[newVersion.uri] = newVersion;
  }

  /**
   * Gets a usecase based on URI using the session cache.
   * @param uri Uri of the usecase
   * @returns The usecase, typically cached in session.
   */
  public getUsecase(uri: string): Promise<SessionUsecase> {
    // Note that current implementation is not completely optimal: If several queries to one usecase uri
    // are made before first query returns, several http calls are made.

    uri = StorageLogic.getLocalUri(uri);
    const usecasesInProduct = ["/UsecaseDefinition/FI53POYS0025543933/costcenter-percentages"];
    if (usecasesInProduct.indexOf(uri) >= 0) {
      return Promise.resolve({
        isLoaded: true,
        exists: true,
        uri: uri,
      });
    }
    const usecase = this.cachedUsecases[uri];
    if (usecase) {
      if (usecase.exists) {
        return Promise.resolve(usecase);
      }
      // Just a marker that the item has already been checked from server.
      return Promise.resolve(null);
    }
    return this.usecases.getSingle(StorageLogic.getIdAtOwner(uri)).then((usecase: SessionUsecase) => {
      if (usecase) {
        this.setToCache(usecase);
        return usecase;
      } else {
        this.cachedUsecases[uri] = {
          isLoaded: true,
          exists: false,
          uri: uri,
        };
        return null;
      }
    });
  }

  private cachedUsecases: { [key: string]: SessionUsecase } = {};
}